import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Rating,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { helpPageStyles } from "../../core/styles/cards/helpPageStyles";
import {
  SUPPORT_EMAIL,
  FEEDBACK_TYPES,
  submitViaEmail,
  getCurrentUser,
  userDisplayName,
  userEmail,
} from "../configs/helpConfig";

/**
 * Feedback form. A low-friction way for staff to share ideas, flag confusing
 * things, or give praise — with an optional star rating. Same submission channel
 * as the support form.
 */
const FeedbackForm = () => {
  const theme = useTheme();
  const styles = helpPageStyles(theme);
  const user = getCurrentUser();

  const [type, setType] = useState(FEEDBACK_TYPES[0]);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [snack, setSnack] = useState(false);

  const canSubmit = message.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const reporter = userDisplayName(user);
    const email = userEmail(user);
    const body =
      `Type: ${type}\n` +
      (rating ? `Rating: ${rating}/5\n` : "") +
      `From: ${reporter || "(name not set)"}${email ? ` <${email}>` : ""}\n` +
      `\n${message}\n`;
    submitViaEmail(`[Feedback] ${type}`, body);
    setSnack(true);
    setMessage("");
    setRating(0);
  };

  return (
    <Box sx={styles.mdFormCard}>
      <Typography sx={styles.mdFormTitle}>Share feedback</Typography>
      <Typography sx={styles.mdFormSubtitle}>
        Ideas, rough edges, or something you love — we read all of it and it
        shapes what we build next.
      </Typography>

      <TextField
        select
        fullWidth
        size="small"
        label="Type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        sx={styles.mdFormField}
      >
        {FEEDBACK_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={styles.mdRatingRow}>
        <Typography sx={styles.mdRatingLabel}>
          How's your experience? (optional)
        </Typography>
        <Rating
          value={rating}
          onChange={(e, v) => setRating(v || 0)}
        />
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Your feedback"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={styles.mdFormField}
      />

      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={handleSubmit}
        sx={styles.mdFormButton}
      >
        Send feedback
      </Button>

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={() => setSnack(false)}
      >
        <Alert severity="success" onClose={() => setSnack(false)} sx={{ width: "100%" }}>
          Thanks! Opening your email app to send this. If nothing opened, email{" "}
          {SUPPORT_EMAIL}.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackForm;
